import React, { useEffect, useMemo, useState } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useAreaList, useCategoryConfigMap, useStatus, useSubAreaList, useSubCategoryConfigMap } from "../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface FilterOption {
  label: string;
  value: string;
}

interface MemberFilters {
  area: string[];
  subArea: string[];
  category: string[];
  subCategory: string[];
  status: string;
}

interface MemberFilterProps {
  area: string[];
  subArea: string[];
  category: string[];
  subCategory: string[];
  status: string;
  areaList: FilterOption[];
  subAreaList: FilterOption[];
  categoryList: FilterOption[];
  subCategoryList: FilterOption[];
  statusList: FilterOption[];
  onApplyFilters: (filters: MemberFilters) => void;
  onResetFilters: () => void;
}

export const MemberFilter: React.FC<MemberFilterProps> = ({
  area,
  subArea,
  category,
  subCategory,
  status,
  areaList,
  subAreaList,
  categoryList,
  subCategoryList,
  statusList,
  onApplyFilters,
  onResetFilters,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { data: statusesResponse } = useStatus({
    remoteUtilityId: Number(remoteUtilityId),
  });
  const [areaValue, setAreaValue] = useState<string>("");
  const [categoryValue, setCategoryValue] = useState<any>([]);

  const { data: areaData } = useAreaList();
  const { data: subareaData } = useSubAreaList(areaValue);
  const { data: categoryData } = useCategoryConfigMap();
  console.log("categoryData", categoryData);
  const { data: subCategoryData } = useSubCategoryConfigMap(categoryValue);
  console.log("areaData",JSON.stringify(areaData) );

const areaOptions = useMemo(
  () =>
    areaData?.map((area: any) => ({
      value: area.id,
      label: area.name,
    })) || [],
  [areaData] 
);
  const subAreaOptions = useMemo(
    () =>
      subareaData?.map((subarea: any) => ({
        value: subarea.id,
        label: subarea.name,
      })),
    [subareaData]
  );

  const categoryOptions = useMemo(
    () =>
      categoryData?.map((item) => ({
        value: item.id,
        label: item.name,
      })) || [],
    [categoryData]
  );

  const subCategoryOptions = useMemo(
    () =>
      subCategoryData?.map((item) => ({
        value: item.id,
        label: item.name,
      })) || [],
    [subCategoryData]
  );

  const statusOptions = useMemo(
    () =>
      statusesResponse?.result?.map((item) => ({
        value: item.value,
        label: item.value,
      })) || [],
    [statusesResponse]
  );

  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "area",
        label: "Area",
        type: "multi-select",
        placeholder: "Select Area(s)",
        options: areaOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "subArea",
        label: "Sub Area",
        type: "multi-select",
        placeholder: "Select Sub Area(s)",
        options: subAreaOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "category",
        label: "Category",
        type: "multi-select",
        placeholder: "Select Category(s)",
        options: categoryOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "subCategory",
        label: "Sub Category",
        type: "multi-select",
        placeholder: "Select Sub Category(s)",
        options: subCategoryOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "Select Status",
        options: statusOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
    ],
    [
      areaOptions,
      subAreaOptions,
      categoryOptions,
      subCategoryOptions,
      statusOptions,
    ]
  );

  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      area: area || [],
      subArea: subArea || [],
      category: category || [],
      subCategory: subCategory || [],
      status: status || "",
    },
  });

  // Update form when filter props change (for auto-population in edit mode)
  useEffect(() => {
    form.reset({
      area: area || [],
      subArea: subArea || [],
      category: category || [],
      subCategory: subCategory || [],
      status: status || "",
    });
  }, [area, subArea, category, subCategory, status, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();

    onApplyFilters({
      area: formValues.area || [],
      subArea: formValues.subArea || [],
      category: formValues.category || [],
      subCategory: formValues.subCategory || [],
      status: formValues.status || "",
    });
  };

  const handleFilterReset = () => {
    form.reset({
      area: [],
      subArea: [],
      category: [],
      subCategory: [],
      status: "",
    });

    onResetFilters();
  };

  const handleFieldChange = (field: string, value: string) => {
    switch(field){
      case "area":
        setAreaValue(value);
        break;
      case "category":
        setCategoryValue(value);
        break;
      default:
        break;
    }
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={2}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-4",
          },
        }}
      />
    </div>
  );
};
